import {chain} from 'lodash';
import React, {FC, useMemo} from 'react';

import {batchStore} from '../../../../libs/shared/core/src/helpers/batching/batching';
import {BannerTypesEnum} from '../../../../libs/shared/core/src/store/banners/commonBannerState';
import {SuccessBannerTypesEnum} from '../../../../libs/shared/core/src/store/banners/commonBannerTypes';
import {AppPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  AppParametricSelector,
  createSelector,
  wrapInShallowSelector,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {AppState} from '../../../store/appState';
import {AppPanelProps} from '../../../store/panels/app/appPanelSelectors';
import {hideBannerAction} from '../../../store/panels/app/banners/bannersActions';
import {makeGetVisibleBannersForPanel} from '../../../store/panels/app/banners/bannersSelectors';
import {LeftSandboxBanner} from './leftSandboxBanner';

/*
 * Props.
 */

interface LeftSandboxBannerContainerProps {
  panelId: AppPanelId;
}

/*
 * Redux.
 */

function makeGetLeftSandboxBannerUids(): AppParametricSelector<AppPanelProps, ReadonlyArray<string>> {
  return wrapInShallowSelector(
    createSelector([makeGetVisibleBannersForPanel()], (visibleBanners) =>
      chain(visibleBanners)
        .filter(
          (banner) =>
            banner.type === BannerTypesEnum.SUCCESS &&
            banner.successType === SuccessBannerTypesEnum.LEFT_SANDBOX,
        )
        .map((banner) => banner.uid)
        .value(),
    ),
  );
}

/*
 * Component.
 */

export const LeftSandboxBannerContainer: FC<LeftSandboxBannerContainerProps> = (props) => {
  const dispatch = useAppDispatch();

  const getBannerUids = useMemo(makeGetLeftSandboxBannerUids, []);
  const bannerUids = useAppSelector((state: AppState) => getBannerUids(state, props));

  if (bannerUids.length === 0) {
    return null;
  }

  const onRequestClose = () => {
    batchStore(() => {
      for (const bannerUid of bannerUids) {
        dispatch(hideBannerAction(bannerUid));
      }
    });
  };

  return <LeftSandboxBanner onRequestClose={onRequestClose} />;
};
